import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { formatMinor } from '@/lib/money';
import type { FinanceAccount, FinancePairBalance } from '@/api/generated/types.gen';

// FinanceAccount['kind'] spelled for people. A kind the API adds later shows
// as its raw code rather than as nothing.
const KIND_LABEL: Record<FinanceAccount['kind'], string> = {
  bank: 'Bank',
  cash: 'Cash',
  paypal: 'PayPal',
  payoneer: 'Payoneer',
  other: 'Other',
};

export interface AccountsTableProps {
  accounts: readonly FinanceAccount[];
  /** The balance grid from /finance/balances; omitted renders names only. */
  balances?: readonly FinancePairBalance[];
}

export function AccountsTable({ accounts, balances }: AccountsTableProps) {
  if (accounts.length === 0) {
    return (
      <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-8 text-center text-sm text-muted">
        No accounts yet. An account is a place money sits — a bank account, the
        cash box, a PayPal balance.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
            <th className="px-4 py-2.5 font-semibold">Account</th>
            <th className="px-4 py-2.5 font-semibold">Kind</th>
            <th className="px-4 py-2.5 font-semibold">Currencies</th>
            <th className="px-4 py-2.5 font-semibold">Balance</th>
            <th className="px-4 py-2.5 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.id} className="border-t border-[var(--border-hairline)] hover:bg-[var(--surface-sunken)]">
              <td className="px-4 py-3">
                <Link
                  to="/finance/accounts/$id"
                  params={{ id: a.id }}
                  className="font-medium text-primary hover:underline"
                >
                  {a.name}
                </Link>
              </td>
              <td className="px-4 py-3">{KIND_LABEL[a.kind] ?? a.kind}</td>
              <td className="px-4 py-3">
                <span className="flex flex-wrap gap-1">
                  {a.currencies.map((c) => (
                    <span
                      key={c}
                      className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] px-1.5 py-0.5 text-xs text-muted"
                    >
                      {c}
                    </span>
                  ))}
                </span>
              </td>
              <td className="px-4 py-3 tabular-nums">
                <AccountBalances account={a} balances={balances} />
              </td>
              <td className="px-4 py-3">
                {a.is_active ? (
                  <Badge variant="success" dot>
                    Active
                  </Badge>
                ) : (
                  <Badge variant="neutral" dot>
                    Inactive
                  </Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One line per currency the account holds. A pair with no row in the grid has
// not been touched on that date; a pair the server marked `estimated` has no
// baseline on or before it (§5.2) and is summed from movements alone, so it
// is shown with a marker rather than as a settled fact.
function AccountBalances({
  account,
  balances,
}: {
  account: FinanceAccount;
  balances?: readonly FinancePairBalance[];
}) {
  if (!balances) return <span className="text-muted">—</span>;
  const mine = account.currencies.map((c) => ({
    currency: c,
    row: balances.find((b) => b.account_id === account.id && b.currency === c),
  }));
  if (mine.every((m) => !m.row)) return <span className="text-muted">—</span>;
  return (
    <span className="flex flex-col gap-0.5">
      {mine.map(({ currency, row }) => (
        <span key={currency} className="flex items-center gap-1">
          {row ? formatMinor(row.balance_minor, currency) : <span className="text-muted">—</span>}
          {row?.estimated && (
            <span
              title="Estimated — no baseline on or before this date, so this is summed from payments alone"
              aria-label="estimated"
              className="text-xs text-[var(--ollio-gold-deep)]"
            >
              ≈
            </span>
          )}
        </span>
      ))}
    </span>
  );
}
